import React, { useCallback, useMemo } from 'react'
import { SyncStatusView } from '../../../apps/professional-app/fa/MeetingSummary/ExternalUpdates/models'
import './IntegrationSavedModalBody.scss'
import { Button } from '../../Button/Button'
import classNames from 'classnames'
import { ActionSuggestionItem } from '../../ActionSuggestion/ActionSuggestionItem'
import { getIntegrationDisplayName, IntegrationServiceId } from '../../../common/integrations'
import { useAppSelector } from '../../../store/hooks'
import { selectCurrentUser } from '../../../store/meetingSummary/user'
import { useActionSuggestions } from '../../ActionSuggestion/hooks/hooks'
import { IntegrationSavedIconCombo } from '../IntegrationSavedIconCombo/IntegrationSavedIconCombo'
import { IntegrationSavedHistoryEntry } from '../IntegrationSavedHistoryEntry/IntegrationSavedHistoryEntry'
import { useConsolidateTasksSyncedTogether } from '../useConsolidateTasksSyncedTogether'
import { useSyncStatuses } from '../../../apps/professional-app/fa/MeetingSummary/ExternalUpdates/hooks'
import { getExternalLink } from '../../../apps/professional-app/fa/MeetingSummary/ExternalUpdates/utils'
import { TrackEvent, useTracking } from '../../../apps/professional-app/fa/common/tracking'

interface IntegrationSavedModalBodyProps {
  syncStatuses: SyncStatusView[]
  serviceId: IntegrationServiceId
  syncTime?: Date | null
  onHide: () => void
  fullWidth?: boolean
  fullHeight?: boolean
  showSuggestions?: boolean
  showSidebar?: boolean
  banner?: React.ReactNode
  showCloseButton?: boolean
  compact?: boolean
}

export const IntegrationSavedModalBody: React.FC<IntegrationSavedModalBodyProps> = ({
  syncStatuses,
  syncTime,
  serviceId,
  onHide,
  fullWidth = false,
  fullHeight = false,
  showSuggestions = true,
  showSidebar = true,
  banner,
  showCloseButton = true,
  compact,
}) => {
  const isCompact = compact ?? !showSidebar
  const currentUser = useAppSelector(selectCurrentUser)

  const newSyncStatuses = useMemo(() => {
    return syncStatuses.filter((status) => !syncTime || syncTime < new Date(status.syncedAt))
  }, [syncTime, syncStatuses])

  const hasNewSync = newSyncStatuses.length > 0

  const { suggestions } = useActionSuggestions()
  const { syncStatuses: allSyncStatuses } = useSyncStatuses()
  const consolidatedSyncStatuses = useConsolidateTasksSyncedTogether(allSyncStatuses)

  const { trackingClient } = useTracking()

  const onClickOpenExternalLink = useCallback(
    (serviceId: string) =>
      trackingClient.track({
        eventName: TrackEvent.CRM_LINK_CLICKED,
        eventDetails: {
          serviceId: serviceId,
          source: 'workflow',
        },
      }),
    [trackingClient],
  )

  const getTitle = () => {
    if (!hasNewSync) {
      return <>Saved to {getIntegrationDisplayName(serviceId)}</>
    }

    const status = newSyncStatuses[0]
    if (newSyncStatuses.length === 1) {
      switch (status?.type) {
        case 'export':
          return <div>The 'Meeting' was successfully saved to {getIntegrationDisplayName(serviceId)}</div>
        case 'meetingSummary':
          return <div>The 'Meeting summary' was successfully saved to {getIntegrationDisplayName(serviceId)}</div>
        case 'aiResult':
          return (
            <div>
              The '{status.name}' was successfully saved to {getIntegrationDisplayName(serviceId)}
            </div>
          )
        case 'task':
          return <div>Saved 'Task' to {getIntegrationDisplayName(serviceId)}</div>
        case 'updateContact':
          return (
            <div>
              Updated '{status.name}' in {getIntegrationDisplayName(serviceId)}
            </div>
          )
        case 'createContact':
          return (
            <div>
              Created '{status.name}' in {getIntegrationDisplayName(serviceId)}
            </div>
          )
      }
    }

    switch (status?.type) {
      case 'task':
        return (
          <div>
            Successfully saved {newSyncStatuses.length} tasks to {getIntegrationDisplayName(serviceId)}
          </div>
        )
      default:
        return (
          <div>
            Successfully saved {newSyncStatuses.length} items to {getIntegrationDisplayName(serviceId)}
          </div>
        )
    }
  }

  const externalLink = hasNewSync ? getExternalLink({ syncStatus: newSyncStatuses[0], currentUser }) : null

  return (
    <div
      className={classNames('integration-saved-modal', 'p-4', 'd-flex', 'flex-column', 'bg-white', 'rounded-3', 'shadow-sm', {
        'integration-saved-modal--tall': !isCompact && !fullHeight,
        'integration-saved-modal--full-height': !isCompact && fullHeight,
        'integration-saved-modal--compact': isCompact,
      })}
    >
      <div className='integration-saved-modal__content integration-saved-modal__content--full-height d-flex gap-3 flex-grow-1'>
        <div className='d-flex flex-column gap-3 flex-grow-1 mw-0'>
          <div
            className={classNames(
              'integration-saved-modal__left',
              'flex-grow-1',
              'rounded-3',
              'p-4',
              'text-center',
              'd-flex',
              'flex-column',
              'align-items-center',
              'justify-content-center',
              { 'full-width': fullWidth },
            )}
          >
            <div className='integration-saved-modal__icons'>
              <IntegrationSavedIconCombo serviceId={serviceId} />
            </div>

            <h3 className='integration-saved-modal__title'>{getTitle()}</h3>

            {hasNewSync && externalLink && (
              <a
                href={externalLink}
                target='_blank'
                rel='noopener noreferrer'
                className='integration-saved-modal__link'
                onClick={() => onClickOpenExternalLink(serviceId)}
              >
                Open in {getIntegrationDisplayName(serviceId)}
              </a>
            )}
          </div>

          {banner}
        </div>

        {showSidebar ? (
          <div className='integration-saved-modal__right integration-saved-modal__right--full-height w-40 d-flex flex-column'>
            <div className='integration-saved-modal__section d-flex flex-column flex-grow-1'>
              <h4 className='integration-saved-modal__section-title'>Updated external systems</h4>
              <div className='integration-saved-modal__scrollable flex-grow-1'>
                {consolidatedSyncStatuses.length > 0 ? (
                  consolidatedSyncStatuses.map((syncStatus, index) => (
                    <React.Fragment key={`${syncStatus.type}-${syncStatus.serviceId}-${syncStatus.syncedAt}-${syncStatus.externalId}`}>
                      <div className='my-4'>
                        <IntegrationSavedHistoryEntry syncStatus={syncStatus} />
                      </div>
                      {index < consolidatedSyncStatuses.length - 1 && <hr className='zocks-bg-color-gray-200 m-0' />}
                    </React.Fragment>
                  ))
                ) : (
                  <div className='text-muted text-center my-4'>No updates.</div>
                )}
              </div>
            </div>

            {showSuggestions && (
              <div className='integration-saved-modal__section d-flex flex-column flex-grow-1'>
                <h4 className='integration-saved-modal__section-title'>Recommended next steps</h4>
                <div className='integration-saved-modal__scrollable flex-grow-1'>
                  <div className='integration-saved-modal__buttons'>
                    {suggestions.map((suggestion) => (
                      <ActionSuggestionItem key={`${suggestion.actionType}-${suggestion.serviceId}`} actionSuggestion={suggestion} onClick={onHide} />
                    ))}
                  </div>
                </div>
              </div>
            )}

            <div className='integration-saved-modal__footer mt-3 d-flex justify-content-end'>
              <Button variant='primary' onClick={onHide}>
                Close
              </Button>
            </div>
          </div>
        ) : null}
      </div>

      {!showSidebar && showCloseButton ? (
        <div className='integration-saved-modal__footer mt-4 d-flex justify-content-end'>
          <Button variant='primary' onClick={onHide}>
            Close
          </Button>
        </div>
      ) : null}
    </div>
  )
}
